"use client"

import { useEffect, useState } from "react"

import { usePermissions } from "@/hooks/use-permissions"
import { getLeadStats } from "@/lib/leads-api"
import { getAppointmentStats } from "@/lib/appointments-api"
import { getQuoteStats } from "@/lib/quotes-api"
import type { AppointmentStats, LeadStats, QuoteStats } from "@/types"

const LEAD_VIEW_PERMISSIONS = ["leads.view.assigned", "leads.view.all"]

export interface DashboardStatsData {
  leadStats: LeadStats | null
  appointmentStats: AppointmentStats | null
  quoteStats: QuoteStats | null
}

export interface UseDashboardStatsResult {
  data: DashboardStatsData
  isLoading: boolean
  /** Holds `leads.view.assigned` OR `leads.view.all`. */
  canViewLeads: boolean
  canViewAppointments: boolean
  canViewQuotes: boolean
  /**
   * True when at least one of the three sources is granted — the condition
   * under which the shared `<DashboardStats />` band should render at all.
   */
  showStats: boolean
}

/**
 * Shared loader behind the headline `<DashboardStats />` band, used by every
 * role variant so each one does not re-implement the same three fetches.
 *
 * Permission contract: nothing is requested until `usePermissions().isLoaded`
 * is true, and each of the three stats calls is skipped (left `null`) when the
 * caller lacks its grant — so an ungranted source never produces a 403 toast
 * and its widget can simply be omitted.
 */
export function useDashboardStats(): UseDashboardStatsResult {
  const { isLoaded, can, canAny } = usePermissions()

  const canViewLeads = canAny(LEAD_VIEW_PERMISSIONS)
  const canViewAppointments = can("appointments.view")
  const canViewQuotes = can("quotes.view")

  const [data, setData] = useState<DashboardStatsData>({
    leadStats: null,
    appointmentStats: null,
    quoteStats: null,
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isLoaded) return

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const [leadStats, appointmentStats, quoteStats] = await Promise.all([
          canViewLeads ? getLeadStats().catch(() => null) : Promise.resolve(null),
          canViewAppointments
            ? getAppointmentStats().catch(() => null)
            : Promise.resolve(null),
          canViewQuotes ? getQuoteStats().catch(() => null) : Promise.resolve(null),
        ])
        if (!cancelled) setData({ leadStats, appointmentStats, quoteStats })
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [isLoaded, canViewLeads, canViewAppointments, canViewQuotes])

  return {
    data,
    isLoading,
    canViewLeads,
    canViewAppointments,
    canViewQuotes,
    showStats: canViewLeads || canViewAppointments || canViewQuotes,
  }
}
